import {
  PopperProps as PopperOwnProps,
  ClickAwayListener,
  Popper as MUIPopper,
  PopperProps as MuiPopperProps,
  styled,
  SxProps,
  Theme,
} from '@nexelem/deprecated-theme';
import PopupState from 'material-ui-popup-state';
import { PopupState as PopupStateType } from 'material-ui-popup-state/hooks';
import { FocusEvent, MouseEvent, ReactNode, useMemo, useRef, useId } from 'react';
import { usePopperStore } from './popperStore';

export type PopperContent = ReactNode | ((props: { popupState: PopupStateType }) => ReactNode);
export type PopperOpeningElement = ReactNode | ((props: { popupState: PopupStateType }) => ReactNode);

export type PopperProps = {
  disabled?: boolean;
  content: PopperContent;
  openingElement: PopperOpeningElement;
  placement?: MuiPopperProps['placement'];
  hasBorder?: boolean;
  id?: string;
  disablePortal?: boolean;
  additionalStyles?: SxProps;
  keepOpenOnFocus?: boolean;
  withoutBackground?: boolean;
};

const Content = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasBorder',
})<{ hasBorder: boolean }>(({ theme, hasBorder }) => {
  const typedTheme = theme as Theme;
  const { getColor } = typedTheme.palette;
  const { popper } = typedTheme.client.popper;
  return {
    ...typedTheme.typography.titilliumWebSemiBold12,
    ...typedTheme.palette.elevation.small,
    color: hasBorder ? getColor(popper.textColor) : getColor(popper.textColorTransparentBG),
    borderRadius: typedTheme.spacing(1),
    padding: hasBorder ? typedTheme.spacing(2) : 0,
    backgroundColor: hasBorder ? getColor(popper.tooltipBackgroundColor) : 'transparent',
    whiteSpace: 'pre-line',
  };
});

const ElementPopper = styled('div')<{ isOpen?: boolean; withoutBackground: boolean }>(({
  isOpen,
  theme,
  withoutBackground,
}) => {
  const typedTheme = theme as Theme;
  const { getColor, hexToRGB } = typedTheme.palette;
  const { popper } = typedTheme.client.popper;
  const backgroundColor = hexToRGB(getColor(popper.activeBackgroundElement), 0.9);
  return {
    display: 'inline-flex',
    maxWidth: '100%',
    '.MuiButtonBase-root': {
      backgroundColor: isOpen && !withoutBackground ? backgroundColor : '',
    },
  };
});

export const Popper = ({
  content,
  openingElement,
  additionalStyles,
  id,
  hasBorder = true,
  placement = 'bottom-start',
  disablePortal = false,
  keepOpenOnFocus = false,
  disabled = false,
  withoutBackground = false,
}: PopperProps) => {
  const popperRef: PopperOwnProps['popperRef'] = useRef(null);
  const generatedId = useId();
  const popperId = id || generatedId;

  const { openPopperId, setOpenPopperId } = usePopperStore();
  const isGloballyOpen = openPopperId === popperId;

  const popperSxData = useMemo(() => {
    return {
      zIndex: 1300,
      ...additionalStyles,
    };
  }, [additionalStyles]);

  return (
    <PopupState popupId={id} variant="popper">
      {(popupState) => {
        // Override PopupState methods to use global state
        const handleOpen = (anchorEl?: HTMLElement) => {
          setOpenPopperId(popperId);
          if (anchorEl) {
            popupState.open(anchorEl);
          }
        };

        const handleClose = () => {
          setOpenPopperId(null);
          popupState.close();
        };

        const handleToggle = (anchorEl: HTMLElement) => {
          if (isGloballyOpen) {
            handleClose();
          } else {
            handleOpen(anchorEl);
          }
        };
        const handleOnClick = (event: MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          if (!disabled) {
            if (keepOpenOnFocus) {
              handleOpen(event.currentTarget);
            } else {
              handleToggle(event.currentTarget);
            }
          }
        };

        const handleOnBlur = (event: FocusEvent<HTMLDivElement>) => {
          const isContain = popperRef.current?.state.elements.popper.contains(event.relatedTarget);
          if (!isContain && event.relatedTarget) {
            handleClose();
          }
        };

        return (
          <>
            <ElementPopper
              isOpen={isGloballyOpen}
              withoutBackground={withoutBackground}
              onBlur={handleOnBlur}
              onClick={handleOnClick}
            >
              {typeof openingElement === 'function' ? openingElement({ popupState }) : openingElement}
            </ElementPopper>
            <ClickAwayListener onClickAway={handleClose}>
              <MUIPopper
                anchorEl={popupState.anchorEl}
                disablePortal={disablePortal}
                open={isGloballyOpen}
                placement={placement}
                popperRef={popperRef}
                sx={popperSxData}
              >
                <Content hasBorder={hasBorder}>
                  {typeof content === 'function' ? content({ popupState }) : content}
                </Content>
              </MUIPopper>
            </ClickAwayListener>
          </>
        );
      }}
    </PopupState>
  );
};
